import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { GridCells } from "../../view/grid/GridLayer";
import type { LabelBreakdown, WinAmountLabel } from "../win-amount-label/WinAmountLabel";
import type { WinAmountLabelTuningBase } from "../win-amount-label/WinAmountLabelTuningBase";
import type { WinHighlightTuning } from "./WinHighlightTuning";

/** Builds one win-amount label; null when the game has none. */
export type WinAmountLabelFactory = () => WinAmountLabel | null;

export interface WinAmountLabelsDeps {
  cells: GridCells;
  overlayRoot: Container;
  runner: PresentationTaskRunner;
  create: WinAmountLabelFactory;
  moneyFormatter: { formatMinor(amount: string, options?: { omitSymbol?: boolean }): string };
  tuning: WinHighlightTuning;
  labelTuning: WinAmountLabelTuningBase;
  /** Where a collected substep label's digits land (`tuning.labelCollect`); null = the labels fade. */
  collectTarget: Container | null;
}

interface LabelState {
  label: WinAmountLabel;
  hideStarted: boolean;
  pendingMerge: { finalText: string; breakdown: LabelBreakdown } | null;
  merging: boolean;
}

/** The `substepTotal` mode's one label and its running totals. */
interface SubstepState {
  label: WinAmountLabel | null;
  baseMinor: bigint;
  finalMinor: bigint;
  hasMultiplier: boolean;
  omitSymbol: boolean;
  merging: boolean;
  /** The pop to the multiplied total, once started (a collect waits for it). */
  merge: Promise<void> | null;
}

/**
 * The win-amount labels over the grid. `perWin` mode: one label per win line, shown after its
 * lead-in and hidden after its dwell, a multiplier breakdown merged at once or when the game says
 * (a cannon impact). `substepTotal` mode: one accumulating label per substep whose base total bumps
 * per win and pops to the multiplied total on merge. With `labelCollect` and a collect target the
 * substep label does not fade: it waits through the removal until `collect` flies its digits into
 * the target (a win meter). Amounts are minor-unit BigInts, never numbers.
 */
export class WinAmountLabels {
  private readonly active = new Map<WinAmountLabel, LabelState>();
  /** Bumped by each teardown; pending engine-time waits bail when it changes. */
  private generation = 0;
  private substep: SubstepState | null = null;
  /** The substep label a removal left waiting for its collect. */
  private waiting: SubstepState | null = null;
  /** Labels whose digits are flying to the collect target. */
  private readonly flying = new Set<WinAmountLabel>();

  public constructor(private readonly deps: WinAmountLabelsDeps) {}

  /** A win line: its own label, or a bump of the substep's total. */
  public onWin(indices: number[], winAmount: string, scopeId: string | undefined, breakdown: { base: string; multiplier: string } | undefined, deferMerge: boolean | undefined): void {
    if (this.deps.tuning.labelMode === "substepTotal") this.accumulate(indices, winAmount, scopeId, breakdown);
    else this.schedule(indices, winAmount, scopeId, breakdown, deferMerge);
  }

  /**
   * The deferred merge, when the multiplier lands: the substep label pops to its final total (then lingers and
   * hides, or holds for its collect); each per-win label morphs.
   */
  public mergeActive(): void {
    const s = this.substep;
    if (s && s.label && s.hasMultiplier && !s.merging) {
      const label = s.label;
      const merged = this.mergeSubstep(s, label);
      if (!this.collects) {
        void (async () => {
          await merged;
          await this.deps.runner.sleep(this.deps.tuning.labelMergeLingerMilliseconds / 1000);
          if (!label.destroyed) {
            await label.hide();
            if (!label.destroyed) label.destroy();
          }
        })();
      }
    }
    for (const [label, state] of this.active) {
      if (!state.pendingMerge || state.hideStarted || state.merging) continue;
      const { finalText, breakdown } = state.pendingMerge;
      state.pendingMerge = null;
      state.merging = true;
      void (async () => {
        await label.mergeToFinal(finalText, breakdown);
        await this.deps.runner.sleep(this.deps.tuning.labelMergeLingerMilliseconds / 1000);
        state.merging = false;
        void this.hide(label, state);
      })();
    }
  }

  /**
   * Collect mode: the substep label — waiting since the removal, or still up — flies into the collect target; an
   * unmerged multiplied total merges first, so the amount that flies is the one the target adds. Resolves true as
   * the digits touch the target (it counts then), or false at once with no label to send. The flight's waits run
   * under `scopeId`.
   */
  public async collect(scopeId: string | undefined): Promise<boolean> {
    const tuning = this.deps.tuning.labelCollect;
    const target = this.deps.collectTarget;
    if (!this.collects || !tuning || !target) return false;
    const s = this.waiting ?? this.substep;
    if (!s) return false;
    if (s === this.waiting) {
      this.waiting = null;
    } else {
      // Collected before a removal: the substep ends here, and a label still to show is dropped.
      this.substep = null;
      this.generation += 1;
    }
    const label = s.label;
    if (!label || label.destroyed) return false;
    const merged = s.hasMultiplier && !s.merging ? this.mergeSubstep(s, label) : s.merge;
    if (merged) await merged;
    if (label.destroyed) return false;
    this.flying.add(label);
    return new Promise<boolean>((resolve) => {
      void label.collectTo(target, tuning, scopeId, () => resolve(true)).finally(() => {
        this.flying.delete(label);
        if (!label.destroyed) label.destroy();
        resolve(true);
      });
    });
  }

  /**
   * Every label ends; one mid-merge is skipped unless `force` (the removal runs alongside the merge and must not cut
   * it). Collecting, the removal leaves the substep label waiting for its collect; `force` (the spin-start clear) ends
   * that one and any label still in flight.
   */
  public flush(force = false): void {
    this.generation += 1;
    const substep = this.substep;
    this.substep = null;
    if (force) {
      this.closeWaiting();
      for (const label of this.flying) this.end(label);
    }
    if (substep?.label && !substep.label.destroyed) {
      if (this.collects && !force) {
        this.closeWaiting();
        this.waiting = substep;
      } else if (force || !substep.merging) {
        this.end(substep.label);
      }
    }
    for (const [label, state] of this.active) {
      if (!state.hideStarted && (force || !state.merging)) void this.hide(label, state);
    }
  }

  /** The substep label flies into the collect target instead of fading. */
  private get collects(): boolean {
    return this.deps.tuning.labelMode === "substepTotal" && this.deps.tuning.labelCollect !== undefined && this.deps.collectTarget !== null;
  }

  /** The substep label pops to its multiplied total. */
  private mergeSubstep(s: SubstepState, label: WinAmountLabel): Promise<void> {
    s.merging = true;
    s.merge = label.popReplaceTo(this.formatSubstep(s.finalMinor, s.omitSymbol));
    return s.merge;
  }

  /** A label waiting for a collect that never came fades out. */
  private closeWaiting(): void {
    const waiting = this.waiting;
    this.waiting = null;
    if (waiting?.label && !waiting.label.destroyed) this.end(waiting.label);
  }

  /** A label fades out, then goes. */
  private end(label: WinAmountLabel): void {
    void label.hide().then(() => {
      if (!label.destroyed) label.destroy();
    });
  }

  private accumulate(indices: number[], winAmount: string, scopeId: string | undefined, breakdown: { base: string; multiplier: string } | undefined): void {
    if (!this.substep) {
      this.closeWaiting();
      this.substep = { label: null, baseMinor: 0n, finalMinor: 0n, hasMultiplier: false, omitSymbol: false, merging: false, merge: null };
      this.showSubstepLabel(indices, scopeId);
    }
    const s = this.substep;
    s.baseMinor += BigInt(breakdown ? breakdown.base : winAmount);
    s.finalMinor += BigInt(winAmount);
    if (breakdown) s.hasMultiplier = true;
    if (s.label) void s.label.bumpAmount(this.formatSubstep(s.baseMinor, s.omitSymbol));
  }

  /** The substep's single label after the lead-in, showing the current base total. */
  private showSubstepLabel(indices: number[], scopeId: string | undefined): void {
    const anchor = this.anchor(indices);
    const generation = this.generation;
    void (async () => {
      await this.deps.runner.sleep(this.deps.tuning.labelInStartDelayMilliseconds / 1000, scopeId);
      const s = this.substep;
      if (this.generation !== generation || !s || s.label) return;
      const label = this.mount(anchor);
      if (!label) return;
      s.label = label;
      s.omitSymbol = label.amountOmitsCurrencySymbol;
      label.show(this.formatSubstep(s.baseMinor, s.omitSymbol));
    })();
  }

  private schedule(indices: number[], winAmount: string, scopeId: string | undefined, breakdown: { base: string; multiplier: string } | undefined, deferMerge: boolean | undefined): void {
    const t = this.deps.tuning;
    const anchor = this.anchor(indices);
    const generation = this.generation;
    const created: { state: LabelState | null } = { state: null };
    void (async () => {
      await this.deps.runner.sleep(t.labelInStartDelayMilliseconds / 1000, scopeId);
      if (this.generation !== generation) return;
      const label = this.mount(anchor);
      if (!label) return;
      const options = label.amountOmitsCurrencySymbol ? { omitSymbol: true } : undefined;
      const formatted = this.deps.moneyFormatter.formatMinor(winAmount, options);
      const breakdownText: LabelBreakdown | undefined = breakdown && this.deps.labelTuning.multiplier ? { baseText: this.deps.moneyFormatter.formatMinor(breakdown.base, options), multiplier: breakdown.multiplier } : undefined;
      let pendingMerge: LabelState["pendingMerge"] = null;
      if (breakdownText && deferMerge) {
        label.show(breakdownText.baseText);
        pendingMerge = { finalText: formatted, breakdown: breakdownText };
      } else {
        label.show(formatted, breakdownText);
      }
      created.state = { label, hideStarted: false, pendingMerge, merging: false };
      this.active.set(label, created.state);
    })();
    if (deferMerge) return;
    void (async () => {
      const multiplier = this.deps.labelTuning.multiplier;
      const extra = breakdown && multiplier ? multiplier.holdSeconds + multiplier.popSeconds : 0;
      await this.deps.runner.sleep(t.labelOutStartDelayMilliseconds / 1000 + extra);
      if (this.generation !== generation) return;
      if (created.state) void this.hide(created.state.label, created.state);
    })();
  }

  /** The label's anchor: the grid centre, or the winning cell nearest the cluster's centroid. */
  private anchor(indices: number[]): { x: number; y: number } {
    const cells = this.deps.cells;
    if (this.deps.tuning.labelPlacement === "gridCenter") {
      const total = cells.gridColumnCount * cells.gridRowCount;
      let x = 0;
      let y = 0;
      for (let i = 0; i < total; i++) {
        const p = cells.getCellLocalPosition(i);
        x += p.x;
        y += p.y;
      }
      return { x: x / total, y: y / total };
    }
    const positions = indices.map((index) => cells.getCellLocalPosition(index));
    const cx = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
    const cy = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;
    let best = positions[0]!;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const p of positions) {
      const distance = (p.x - cx) ** 2 + (p.y - cy) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = p;
      }
    }
    return best;
  }

  /** A label parented into the overlay at a grid-local position, with its tuning and runner. */
  private mount(gridLocal: { x: number; y: number }): WinAmountLabel | null {
    const label = this.deps.create();
    if (!label) return null;
    label.node.position.copyFrom(this.deps.overlayRoot.toLocal(gridLocal, this.deps.cells.gridRoot ?? this.deps.overlayRoot));
    this.deps.overlayRoot.addChild(label.node);
    label.setTuning(this.deps.labelTuning);
    label.setTaskRunner(this.deps.runner);
    return label;
  }

  private async hide(label: WinAmountLabel, state: LabelState): Promise<void> {
    if (state.hideStarted) return;
    state.hideStarted = true;
    if (!label.destroyed) await label.hide();
    this.active.delete(label);
    if (!label.destroyed) label.destroy();
  }

  private formatSubstep(minor: bigint, omitSymbol: boolean): string {
    return this.deps.moneyFormatter.formatMinor(minor.toString(), omitSymbol ? { omitSymbol: true } : undefined);
  }
}
